import {
  useEffect,
  useRef,
  useState,
  type KeyboardEventHandler,
  type PointerEventHandler,
} from 'react';

const HOLD_DELAY_MS = 320;
const HOLD_SLOP_PX = 9;
const AUTHORITATIVE_SYNC_TIMEOUT_MS = 2_000;

export function moveReorderItem<T>(items: readonly T[], item: T, target: T): T[] {
  const from = items.indexOf(item);
  const to = items.indexOf(target);
  if (from < 0 || to < 0 || from === to) return [...items];
  const next = [...items];
  next.splice(from, 1);
  next.splice(to, 0, item);
  return next;
}

export function placeReorderItem<T>(
  items: readonly T[],
  item: T,
  target: T,
  position: 'before' | 'after',
): T[] {
  if (item === target || !items.includes(item) || !items.includes(target)) return [...items];
  const next = items.filter((candidate) => candidate !== item);
  const targetIndex = next.indexOf(target);
  next.splice(targetIndex + (position === 'after' ? 1 : 0), 0, item);
  return next;
}

export function useHoldReorder<T extends string>(
  items: readonly T[],
  scope: string,
  enabled: boolean,
  onCommit: (items: T[]) => void,
) {
  const [orderedItems, setOrderedItems] = useState<T[]>(() => [...items]);
  const [draggingItem, setDraggingItem] = useState<T | null>(null);
  const orderedRef = useRef(orderedItems);
  const holdTimerRef = useRef<number | null>(null);
  const reconcileTimerRef = useRef<number | null>(null);
  const finishRef = useRef<(commit: boolean) => void>(() => undefined);
  const movePointerRef = useRef<(event: PointerEvent) => void>(() => undefined);
  const itemsRef = useRef(items);
  const sessionRef = useRef<{
    item: T;
    pointerId: number;
    startX: number;
    startY: number;
    active: boolean;
    handle: HTMLButtonElement;
  } | null>(null);
  const itemKey = items.join('\u0000');

  itemsRef.current = items;

  useEffect(() => {
    if (sessionRef.current?.active) return;
    if (reconcileTimerRef.current !== null) window.clearTimeout(reconcileTimerRef.current);
    reconcileTimerRef.current = null;
    const next = [...items];
    orderedRef.current = next;
    setOrderedItems(next);
  }, [itemKey]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    const finishPointer = (event: PointerEvent) => {
      if (sessionRef.current?.pointerId === event.pointerId) finishRef.current(true);
    };
    const cancelPointer = (event: PointerEvent) => {
      if (sessionRef.current?.pointerId === event.pointerId) finishRef.current(false);
    };
    const movePointer = (event: PointerEvent) => movePointerRef.current(event);
    const cancelActiveSession = () => finishRef.current(false);
    const handleVisibilityChange = () => {
      if (document.visibilityState !== 'visible') cancelActiveSession();
    };

    window.addEventListener('pointerup', finishPointer, true);
    window.addEventListener('pointercancel', cancelPointer, true);
    window.addEventListener('pointermove', movePointer, { capture: true, passive: false });
    window.addEventListener('blur', cancelActiveSession);
    document.addEventListener('visibilitychange', handleVisibilityChange);
    return () => {
      window.removeEventListener('pointerup', finishPointer, true);
      window.removeEventListener('pointercancel', cancelPointer, true);
      window.removeEventListener('pointermove', movePointer, true);
      window.removeEventListener('blur', cancelActiveSession);
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      if (holdTimerRef.current !== null) window.clearTimeout(holdTimerRef.current);
      if (reconcileTimerRef.current !== null) window.clearTimeout(reconcileTimerRef.current);
    };
  }, []);

  function update(next: T[]) {
    orderedRef.current = next;
    setOrderedItems(next);
  }

  function commitOrder(next: T[]) {
    onCommit([...next]);
    if (reconcileTimerRef.current !== null) window.clearTimeout(reconcileTimerRef.current);
    reconcileTimerRef.current = window.setTimeout(() => {
      reconcileTimerRef.current = null;
      update([...itemsRef.current]);
    }, AUTHORITATIVE_SYNC_TIMEOUT_MS);
  }

  function finish(commit: boolean) {
    if (holdTimerRef.current !== null) window.clearTimeout(holdTimerRef.current);
    holdTimerRef.current = null;
    const session = sessionRef.current;
    sessionRef.current = null;
    setDraggingItem(null);
    try {
      if (session?.handle.hasPointerCapture(session.pointerId)) {
        session.handle.releasePointerCapture(session.pointerId);
      }
    } catch {
      // A browser can retire the pointer immediately before dispatching its final capture event.
    }
    if (session?.active && commit) commitOrder(orderedRef.current);
    if (session?.active && !commit) update([...itemsRef.current]);
  }

  finishRef.current = finish;

  function movePointer(event: PointerEvent) {
    const session = sessionRef.current;
    if (!session || session.pointerId !== event.pointerId) return;
    if (!session.active) {
      if (Math.hypot(event.clientX - session.startX, event.clientY - session.startY) > HOLD_SLOP_PX) {
        finish(false);
      }
      return;
    }
    event.preventDefault();
    const scrollContainer = session.handle.closest<HTMLElement>('.concept-overview-groups');
    if (scrollContainer) {
      const bounds = scrollContainer.getBoundingClientRect();
      const edgeSize = Math.min(64, bounds.height * .16);
      if (event.clientY < bounds.top + edgeSize) scrollContainer.scrollBy({ top: -14 });
      if (event.clientY > bounds.bottom - edgeSize) scrollContainer.scrollBy({ top: 14 });
    }
    const target = document.elementFromPoint(event.clientX, event.clientY)
      ?.closest<HTMLElement>(`[data-reorder-scope="${scope}"]`);
    const targetItem = target?.dataset.reorderId as T | undefined;
    if (!target || !targetItem || targetItem === session.item) return;
    const targetDropArea = scope === 'concept-groups'
      ? target.querySelector<HTMLElement>(':scope > header') ?? target
      : target;
    const targetBounds = targetDropArea.getBoundingClientRect();
    const position = event.clientY < targetBounds.top + targetBounds.height / 2
      ? 'before'
      : 'after';
    const next = placeReorderItem(orderedRef.current, session.item, targetItem, position);
    if (next.some((value, index) => value !== orderedRef.current[index])) update(next);
  }

  movePointerRef.current = movePointer;

  function handleProps(item: T) {
    const onPointerDown: PointerEventHandler<HTMLButtonElement> = (event) => {
      if (
        !enabled
        || reconcileTimerRef.current !== null
        || sessionRef.current !== null
        || event.button !== 0
      ) return;
      event.currentTarget.setPointerCapture(event.pointerId);
      sessionRef.current = {
        item,
        pointerId: event.pointerId,
        startX: event.clientX,
        startY: event.clientY,
        active: false,
        handle: event.currentTarget,
      };
      holdTimerRef.current = window.setTimeout(() => {
        const session = sessionRef.current;
        if (!session || session.pointerId !== event.pointerId) return;
        session.active = true;
        setDraggingItem(item);
      }, HOLD_DELAY_MS);
    };
    const onPointerUp: PointerEventHandler<HTMLButtonElement> = () => finish(true);
    const onPointerCancel: PointerEventHandler<HTMLButtonElement> = () => finish(false);
    const onKeyDown: KeyboardEventHandler<HTMLButtonElement> = (event) => {
      if (
        !enabled
        || reconcileTimerRef.current !== null
        || (event.key !== 'ArrowUp' && event.key !== 'ArrowDown')
      ) return;
      event.preventDefault();
      const index = orderedRef.current.indexOf(item);
      const targetIndex = event.key === 'ArrowUp' ? index - 1 : index + 1;
      const target = orderedRef.current[targetIndex];
      if (!target) return;
      const next = moveReorderItem(orderedRef.current, item, target);
      update(next);
      commitOrder(next);
    };
    return {
      onKeyDown,
      onPointerCancel,
      onPointerDown,
      onPointerUp,
    };
  }

  return { draggingItem, handleProps, orderedItems };
}
